import { t as uiText } from '../i18n';
// src/components/DRSView.tsx
import React, { useEffect, useRef, useState } from 'react';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, serverTimestamp, deleteDoc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { executeDeepReasoningJob, subscribeDeepReasoningBatchRun, type BatchRunData } from '../api/deepReasoningJob';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useWorkspaceEntitlements } from '../features/plans/useWorkspaceEntitlements';

interface DRSViewProps {
  workspaceId?: string;
  boardId?: string;
  title?: string;
  languageCode?: string;  // 言語コード（指定時は新パス、未指定時は旧パス）
}

interface PublicPageData {
  status?: string;
  published?: boolean;
  [key: string]: any;
}

interface SlotData {
  default?: boolean;
  equivalents?: string[];
  presence?: string;
}

interface ActionData {
  default?: boolean;
  equivalents?: string[];
  presence?: string;
}

interface TargetData {
  default?: boolean;
  equivalents?: string[];
  presence?: string;
}

interface SlotsData {
  action?: ActionData[];
  target?: TargetData[];
  [key: string]: any;
}

interface DRSData {
  pageId: string;
  docId?: string; // FirestoreドキュメントID
  defaultText: string;
  template?: string;
  editable?: boolean;
  slots?: SlotsData;
  [key: string]: any;
}

interface TemplateToken {
  type: 'text' | 'key';
  value: string;
  key?: string; // keyの場合のみ
}

// Template解析用のユーティリティ関数
const parseTemplate = (template: string): TemplateToken[] => {
  const tokens: TemplateToken[] = [];
  const regex = /\{([^}]+)\}/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(template)) !== null) {
    // キーの前のテキスト
    if (match.index > lastIndex) {
      tokens.push({
        type: 'text',
        value: template.slice(lastIndex, match.index)
      });
    }
    
    // キー
    tokens.push({
      type: 'key',
      value: match[0], // {key}
      key: match[1]    // key
    });
    
    lastIndex = regex.lastIndex;
  }
  
  // 最後のテキスト
  if (lastIndex < template.length) {
    tokens.push({
      type: 'text',
      value: template.slice(lastIndex)
    });
  }
  
  return tokens;
};

const tokensToTemplate = (tokens: TemplateToken[]): string => {
  return tokens.map(token => token.value).join('');
};

// Templateからキーの順序を取得する関数
const getKeyOrderFromTemplate = (template: string): string[] => {
  const keys: string[] = [];
  const regex = /\{([^}]+)\}/g;
  let match;
  
  while ((match = regex.exec(template)) !== null) {
    const key = match[1];
    if (!keys.includes(key)) {
      keys.push(key);
    }
  }
  
  return keys;
};

// ランダムな16文字を生成する関数
const generateRandomHash16 = (): string => {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 16; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// DRSデータのドキュメントIDを生成する関数
const generateDRSDocumentId = (pageId: string, drsData: DRSData): string => {
  // 内容が空の場合はランダムハッシュを使用
  if (!drsData.template && !drsData.defaultText && !drsData.slots) {
    const randomHash = generateRandomHash16();
    return `${pageId}_${randomHash}`;
  }
  
  // 内容がある場合はハッシュを生成（簡易版）
  const contentString = JSON.stringify({
    template: drsData.template || '',
    defaultText: drsData.defaultText || '',
    slots: drsData.slots || {}
  });
  
  // 簡易ハッシュ生成（実際のcryptoは使わず、文字列のハッシュ値を計算）
  let hash = 0;
  for (let i = 0; i < contentString.length; i++) {
    const char = contentString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // 32bit整数に変換
  }
  
  // ハッシュ値を16進数文字列に変換して16文字に短縮
  const hashString = Math.abs(hash).toString(16).padStart(8, '0');
  const hash16 = hashString.substring(0, 16).padEnd(16, '0');
  
  return `${pageId}_${hash16}`;
};

// SlotsをTemplateのキー順に並び替える関数
const sortSlotsByTemplateOrder = (slots: SlotsData, template: string): [string, any][] => {
  const keyOrder = getKeyOrderFromTemplate(template);
  const slotEntries = Object.entries(slots);
  
  // Templateに含まれるキーを順序通りに並べる
  const orderedSlots: [string, any][] = [];
  keyOrder.forEach(key => {
    const slot = slotEntries.find(([slotKey]) => slotKey === key);
    if (slot) {
      orderedSlots.push(slot);
    }
  });
  
  // Templateに含まれていないキーを最後に追加
  slotEntries.forEach(([key, value]) => {
    if (!keyOrder.includes(key)) {
      orderedSlots.push([key, value]);
    }
  });
  
  return orderedSlots;
};

// テキスト挿入用のコンポーネント
const TextInsertButton: React.FC<{
  onInsert: (text: string) => void;
  position: string;
}> = ({ onInsert, position }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [text, setText] = useState('');

  const handleSubmit = () => {
    if (text.trim()) {
      onInsert(text.trim());
      setText('');
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setText('');
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {!isOpen ? (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          style={{
            fontSize: '11px',
            padding: '2px 6px',
            border: '1px dashed #ccc',
            borderRadius: '4px',
            backgroundColor: '#f9f9f9',
            cursor: 'pointer',
            color: '#666',
            minWidth: '60px'
          }}
          title={uiText("Add text at {{position}}", { position: position })}
        >
          {uiText("+ Text")}
        </button>
      ) : (
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSubmit}
          autoFocus
          placeholder={uiText("Enter text")}
          style={{
            fontSize: '12px',
            padding: '2px 6px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            backgroundColor: '#fff',
            minWidth: '100px'
          }}
        />
      )}
    </div>
  );
};

// ドラッグ可能なキーコンポーネント
// ドラッグ可能な平文テキスト用のコンポーネント
const DraggableText: React.FC<{
  token: TemplateToken;
  tokenIndex: number;
  onUpdate: (newValue: string) => void;
  onDelete: () => void;
}> = ({ token, tokenIndex, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(token.value);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: `token-${token.value}-${tokenIndex}`,
    data: {
      token,
      tokenIndex
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleSave = () => {
    onUpdate(editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(token.value);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          style={{
            fontSize: '12px',
            padding: '2px 4px',
            border: '1px solid var(--workspace-primary, #cfae91)',
            borderRadius: '2px',
            backgroundColor: '#ffffff',
            minWidth: '60px'
          }}
          autoFocus
        />
        <button
          onClick={handleSave}
          style={{
            fontSize: '10px',
            padding: '1px 4px',
            backgroundColor: 'var(--workspace-primary, #cfae91)',
            color: 'white',
            border: 'none',
            borderRadius: '2px',
            cursor: 'pointer'
          }}
        >
          ✓
        </button>
        <button
          onClick={handleCancel}
          style={{
            fontSize: '10px',
            padding: '1px 4px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '2px',
            cursor: 'pointer'
          }}
        >
          ×
        </button>
      </span>
    );
  }

  return (
    <span
      ref={setNodeRef}
      style={{
        ...style,
        backgroundColor: '#ffffff',
        borderRadius: '4px',
        padding: '2px 6px',
        fontSize: '12px',
        color: '#495057',
        cursor: 'grab',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        userSelect: 'none',
        border: '1px solid #e9ecef'
      }}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        e.stopPropagation();
        setIsEditing(true);
      }}
      title={uiText("Click {{value}} to edit, drag to move", { value: token.value })}
    >
      {token.value}
      <span 
        style={{ 
          fontSize: '10px', 
          color: '#d32f2f',
          cursor: 'pointer',
          padding: '2px',
          borderRadius: '2px',
          backgroundColor: 'rgba(211, 47, 47, 0.1)'
        }}
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        onPointerDown={(e) => {
          e.stopPropagation();
        }}
        title={uiText("Delete {{value}}", { value: token.value })}
      >
        ×
      </span>
    </span>
  );
};

const DraggableKey: React.FC<{
  token: TemplateToken;
  tokenIndex: number;
  onDelete: () => void;
}> = ({ token, tokenIndex, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: `token-${token.value}-${tokenIndex}`,
    data: {
      token,
      tokenIndex
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <span
      ref={setNodeRef}
      style={{
        ...style,
        backgroundColor: 'var(--workspace-border-soft, #f5f0eb)',
        borderRadius: '4px',
        padding: '2px 6px',
        fontSize: '12px',
        color: 'var(--workspace-primary, #cfae91)',
        cursor: 'grab',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        userSelect: 'none',
      }}
      {...attributes}
      {...listeners}
      title={uiText("Drag to move {{key}}", { key: token.key })}
    >
      {token.value}
      <span 
        style={{ 
          fontSize: '10px', 
          color: '#d32f2f',
          cursor: 'pointer',
          padding: '2px',
          borderRadius: '2px',
          backgroundColor: 'rgba(211, 47, 47, 0.1)'
        }}
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        onPointerDown={(e) => {
          e.stopPropagation();
        }}
        title={uiText("Delete {{key}}", { key: token.key })}
      >
        ×
      </span>
    </span>
  );
};

// バリデーション関数
const validateDRSData = (data: DRSData): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  // 1. 必須項目の空欄チェック
  if (!data.defaultText || data.defaultText.trim() === '') {
    errors.push(uiText("Default Text is empty"));
  }
  
  if (!data.template || data.template.trim() === '') {
    errors.push(uiText("Template is empty"));
  }
  
  // 2. Slotsの各キーでdefaultがtrueが一つだけかチェック
  if (data.slots) {
    Object.entries(data.slots).forEach(([slotKey, slotValue]) => {
      if (Array.isArray(slotValue)) {
        const defaultTrueCount = slotValue.filter(item => item.default === true).length;
        
        if (defaultTrueCount === 0) {
          errors.push(uiText("Slot \"{{slotKey}}\" has no item with default set to true", { slotKey: slotKey }));
        } else if (defaultTrueCount > 1) {
          errors.push(uiText("Slot \"{{slotKey}}\" has {{defaultTrueCount}} items with default set to true (only 1 is allowed)", { slotKey: slotKey, defaultTrueCount: defaultTrueCount }));
        }
        
        // 3. 各項目の必須フィールドチェック
        slotValue.forEach((item, itemIndex) => {
          if (!item.presence || item.presence.trim() === '') {
            errors.push(uiText("presence is empty for item {{v1}} in slot \"{{slotKey}}\"", { slotKey: slotKey, v1: itemIndex + 1 }));
          }
          
          if (!item.equivalents || item.equivalents.length === 0) {
            errors.push(uiText("equivalents is empty for item {{v1}} in slot \"{{slotKey}}\"", { slotKey: slotKey, v1: itemIndex + 1 }));
          } else {
            item.equivalents.forEach((eq: string, eqIndex: number) => {
              if (!eq || eq.trim() === '') {
                errors.push(uiText("equivalents[{{eqIndex}}] is empty for item {{v1}} in slot \"{{slotKey}}\"", { slotKey: slotKey, v1: itemIndex + 1, eqIndex: eqIndex }));
              }
            });
          }
        });
      }
    });
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

const DRSView: React.FC<DRSViewProps> = ({ workspaceId, boardId, title, languageCode }) => {
  const { can } = useWorkspaceEntitlements();
  const [publicPageData, setPublicPageData] = useState<PublicPageData | null>(null);
  const [drsDataList, setDrsDataList] = useState<DRSData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiGenError, setAiGenError] = useState<string | null>(null);
  const [aiGenSuccess, setAiGenSuccess] = useState<string | null>(null);
  const aiUnsubRef = useRef<null | (() => void)>(null);
  /** props に言語が無いとき、レガシーが空なら settings/drs.defaultLanguage 宛てで読んだ lang（保存パスと揃える） */
  const [resolvedLanguageFallback, setResolvedLanguageFallback] = useState<string | null>(null);

  const explicitLanguageCode = typeof languageCode === 'string' ? languageCode.trim() : '';
  const effectiveLanguageCode = explicitLanguageCode || resolvedLanguageFallback || undefined;

  // ドキュメント参照を取得するヘルパー関数（言語別パス対応）
  const getDrsDocRef = (collectionName: 'publicDRS' | 'nonPublicDRS', docId: string) => {
    if (effectiveLanguageCode) {
      // 言語指定時：新パス
      return doc(
        db,
        'publicPages',
        workspaceId!,
        collectionName,
        boardId!,
        'languages',
        effectiveLanguageCode,
        'suggestions',
        docId
      );
    } else {
      // 言語未指定時：旧パス
      return doc(db, 'publicPages', workspaceId!, collectionName, docId);
    }
  };
  const [editingData, setEditingData] = useState<DRSData | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [showChangeChip, setShowChangeChip] = useState<number | null>(null);

  // ドラッグ&ドロップのセンサー設定
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 開閉状態の切り替え
  const toggleExpanded = (index: number) => {
    const newExpandedItems = new Set(expandedItems);
    if (newExpandedItems.has(index)) {
      newExpandedItems.delete(index);
    } else {
      newExpandedItems.add(index);
    }
    setExpandedItems(newExpandedItems);
  };

  // ドラッグ終了時のハンドラー
  const handleDragEnd = (event: DragEndEvent, index: number) => {
    const { active, over } = event;

    if (active.id !== over?.id && editingData) {
      const template = editingData.template || '';
      const tokens = parseTemplate(template);

      const oldIndex = tokens.findIndex((_, i) => `token-${tokens[i].value}-${i}` === active.id);
      const newIndex = tokens.findIndex((_, i) => `token-${tokens[i].value}-${i}` === over?.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newTokens = arrayMove(tokens, oldIndex, newIndex);
        const newTemplate = tokensToTemplate(newTokens);
        setEditingData({ ...editingData, template: newTemplate });
      }
    }
  };


  // データ取得関数（外部から呼び出し可能）
  const fetchData = async () => {
    if (!workspaceId || !boardId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. 公開ページのステータス情報を取得
      const publicPageRef = doc(db, 'publicPages', workspaceId, 'pages', boardId);
      const publicPageSnap = await getDoc(publicPageRef);

      let pageData: PublicPageData | null = null;
      if (publicPageSnap.exists()) {
        pageData = publicPageSnap.data();
      }
      setPublicPageData(pageData);

      // 2. ステータスに応じてDRSデータを取得
      const isPublic = pageData?.status === 'public' && pageData?.published === true;
      const collectionName = isPublic ? 'publicDRS' : 'nonPublicDRS';

      let drsSnapshot;

      if (explicitLanguageCode) {
        setResolvedLanguageFallback(null);
        // 言語指定時：新パス publicDRS/{boardId}/languages/{lang}/suggestions
        const suggestionsRef = collection(
          db,
          'publicPages',
          workspaceId,
          collectionName,
          boardId,
          'languages',
          explicitLanguageCode,
          'suggestions'
        );
        drsSnapshot = await getDocs(suggestionsRef);
      } else {
        // 言語未指定時：旧パス publicDRS/* where pageId == boardId（互換性維持）
        const drsQuery = query(
          collection(db, 'publicPages', workspaceId, collectionName),
          where('pageId', '==', boardId)
        );
        drsSnapshot = await getDocs(drsQuery);

        // 多言語移行後はデータが languages/{lang}/ にしか無いことがあり、languageCode prop 無し（例: モーダル）だとレガシーだけでは 0 件になる。
        // defaultLanguage 未設定でも availableLanguages があれば順に試す。
        if (drsSnapshot.empty) {
          const settingsRef = doc(db, 'publicPages', workspaceId, 'settings', 'drs');
          const settingsSnap = await getDoc(settingsRef);
          const drsSettings = settingsSnap.exists() ? (settingsSnap.data() as { defaultLanguage?: string; availableLanguages?: string[] }) : null;
          const rawDef = drsSettings ? String(drsSettings.defaultLanguage ?? '').trim() : '';
          const available = Array.isArray(drsSettings?.availableLanguages)
            ? drsSettings.availableLanguages.filter((x): x is string => typeof x === 'string' && x.trim().length > 0).map((x) => x.trim())
            : [];

          const tryLangs: string[] = [];
          if (rawDef && !tryLangs.includes(rawDef)) tryLangs.push(rawDef);
          for (const a of available) {
            if (!tryLangs.includes(a)) tryLangs.push(a);
          }

          let foundLang: string | null = null;
          for (const lang of tryLangs) {
            const fallbackRef = collection(
              db,
              'publicPages',
              workspaceId,
              collectionName,
              boardId,
              'languages',
              lang,
              'suggestions'
            );
            const fbSnap = await getDocs(fallbackRef);
            if (!fbSnap.empty) {
              drsSnapshot = fbSnap;
              foundLang = lang;
              break;
            }
          }
          if (foundLang) {
            setResolvedLanguageFallback(foundLang);
          } else {
            setResolvedLanguageFallback(null);
          }
        } else {
          setResolvedLanguageFallback(null);
        }
      }
      
      if (!drsSnapshot.empty) {
        const drsDataArray: DRSData[] = [];
        drsSnapshot.docs.forEach(doc => {
          const data = doc.data() as DRSData;
          // ドキュメントIDを追加
          data.docId = doc.id;
          drsDataArray.push(data);
        });
        setDrsDataList(drsDataArray);
      } else {
        setDrsDataList([]);
      }

    } catch (err) {
      console.error('データ取得エラー:', err);
      setError(uiText("Failed to fetch data"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setResolvedLanguageFallback(null);
  }, [workspaceId, boardId]);

  // 公開ページのステータス情報とDRSデータを取得
  useEffect(() => {
    fetchData();
  }, [workspaceId, boardId, languageCode]);

  // 画面遷移/アンマウント時に購読を停止
  useEffect(() => {
    return () => {
      aiUnsubRef.current?.();
      aiUnsubRef.current = null;
    };
  }, []);

  const startAIGenerationForThisPage = async () => {
    if (!workspaceId || !boardId) {
      alert(uiText("Workspace ID or board ID not found"));
      return;
    }
    if (aiGenerating) return;

    setAiGenerating(true);
    setAiGenError(null);
    setAiGenSuccess(null);

    // 既存購読があれば停止（連打/再実行対策）
    aiUnsubRef.current?.();
    aiUnsubRef.current = null;

    try {
      const { runId } = await executeDeepReasoningJob({
        workspaceId,
        pageIds: [boardId],
        ...(effectiveLanguageCode ? { languageCode: effectiveLanguageCode } : {}),
        // taskInnerConcurrencyはバックエンドで自動的に2に設定されます（ベストプラクティス）
      });

      aiUnsubRef.current = subscribeDeepReasoningBatchRun({
        workspaceId,
        runId,
        onChange: (data: BatchRunData | null) => {
          if (!data) {
            return;
          }

          const st = String(data.status || '');
          if (st === 'completed' || st === 'completed_with_issues') {
            aiUnsubRef.current?.();
            aiUnsubRef.current = null;
            setAiGenerating(false);
            setAiGenSuccess(uiText("DRS generation for this page is complete."));
            setTimeout(() => setAiGenSuccess(null), 4000);
            // 生成結果を再読み込み
            fetchData();
          }
        },
        onError: (e) => {
          console.error('❌ batchRuns 購読エラー:', e);
          aiUnsubRef.current?.();
          aiUnsubRef.current = null;
          setAiGenerating(false);
          setAiGenError(uiText("Failed to subscribe to progress (check permissions and rules)"));
        }
      });
    } catch (e) {
      console.error('❌ DRS AI生成開始エラー:', e);
      setAiGenerating(false);
      setAiGenError(e instanceof Error ? e.message : uiText("Failed to generate DRS with AI"));
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeOut {
            0% { opacity: 1; }
            70% { opacity: 1; }
            100% { opacity: 0; }
          }
        `}
      </style>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        minHeight: 0,
        flex: '1 1 auto',
        padding: '8px',
        backgroundColor: '#ffffff',
        color: '#6b7280',
        width: '100%',
        boxSizing: 'border-box'
      }}>
      {/* DRSデータ表示 */}
      {drsDataList.length > 0 && (
        <div style={{
          marginBottom: '16px',
          width: '100%'
        }}>
          
          {drsDataList.map((drsData, index) => (
            <div key={index} style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              padding: expandedItems.has(index) ? '16px' : '8px',
              marginBottom: index < drsDataList.length - 1 ? '8px' : '0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '14px',
                fontWeight: '600',
                color: '#495057',
                marginBottom: expandedItems.has(index) ? '16px' : '0',
                paddingBottom: expandedItems.has(index) ? '8px' : '0',
                borderBottom: expandedItems.has(index) ? '1px solid #e9ecef' : 'none'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    onClick={() => toggleExpanded(index)}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      color: '#495057'
                    }}
                    title={expandedItems.has(index) ? uiText("Hide details") : uiText("Show details")}
                  >
                    {expandedItems.has(index) ? '▼' : '▶'}
                  </button>
                  <span>{uiText("Data")} {index + 1}</span>
                  {drsData.defaultText && (
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#495057',
                      maxWidth: '500px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }} title={drsData.defaultText}>
                      {drsData.defaultText}
                    </span>
                  )}
                  {drsData.editable !== undefined && (
                    <span 
                      style={{
                        fontSize: '12px',
                        fontWeight: '500',
                        color: drsData.editable ? 'var(--workspace-primary, #cfae91)' : '#ffffff',
                        backgroundColor: drsData.editable ? '#ffffff' : 'var(--workspace-primary, #cfae91)',
                        padding: '2px 6px',
                        borderRadius: '50px',
                        border: drsData.editable ? '1px solid var(--workspace-primary, #cfae91)' : 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={async () => {
                        if (editingIndex === index) {
                          alert(uiText("This can't be changed while editing. Finish editing and try again."));
                          return;
                        }
                        
                        try {
                          // コレクション名を決定
                          const isPublic = publicPageData?.status === 'public' && publicPageData?.published === true;
                          const collectionName = isPublic ? 'publicDRS' : 'nonPublicDRS';

                          // ドキュメントを更新（言語別パス対応）
                          const docRef = getDrsDocRef(collectionName, drsData.docId || drsData.pageId);
                          await updateDoc(docRef, {
                            editable: !drsData.editable,
                            updatedAt: serverTimestamp()
                          });

                          // データを再読み込み
                          await fetchData();
                          
                          // チップを表示
                          setShowChangeChip(index);
                          setTimeout(() => {
                            setShowChangeChip(null);
                          }, 3000);
                        } catch (error) {
                          console.error('editable更新エラー:', error);
                          alert(uiText("Failed to update editable"));
                        }
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.opacity = '0.8';
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                      title={uiText("Click to change DRS updates to {{v0}}", { v0: drsData.editable ? uiText("Not allowed") : uiText("Allowed") })}
                    >
                      {drsData.editable ? uiText("DRS auto-update allowed") : uiText("DRS auto-update not allowed")}
                    </span>
                  )}
                  {showChangeChip === index && (
                    <span 
                      style={{
                        fontSize: '11px',
                        fontWeight: '500',
                        color: 'var(--workspace-primary, #cfae91)',
                        backgroundColor: '#ffffff',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        marginLeft: '8px',
                        opacity: 1,
                        animation: 'fadeOut 3s ease-out forwards',
                        display: 'inline-block'
                      }}
                    >
                      {uiText("Changed")}
                    </span>
                  )}
                </div>
                <div style={{
                  display: 'flex',
                  gap: '8px'
                }}>
                  <button
                    style={{
                      backgroundColor: editingIndex === index ? '#ffffff' : 'var(--workspace-primary, #cfae91)',
                      color: editingIndex === index ? 'var(--workspace-primary, #cfae91)' : 'white',
                      border: editingIndex === index ? '1px solid var(--workspace-primary, #cfae91)' : 'none',
                      borderRadius: '4px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                    onMouseOver={(e) => {
                      if (editingIndex === index) {
                        e.currentTarget.style.backgroundColor = 'var(--workspace-border-soft, #f5f0eb)';
                      } else {
                        e.currentTarget.style.backgroundColor = 'var(--workspace-primary-dark, #b89a7a)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (editingIndex === index) {
                        e.currentTarget.style.backgroundColor = '#ffffff';
                      } else {
                        e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
                      }
                    }}
                    onClick={() => {
                      if (editingIndex === index) {
                        // キャンセル処理
                        setEditingIndex(null);
                        setEditingData(null);
                      } else {
                        // 編集モード開始
                        const isPublic = publicPageData?.status === 'public' && publicPageData?.published === true;
                        const originalCollection = isPublic ? 'publicDRS' : 'nonPublicDRS';
                        setEditingIndex(index);
                        setEditingData({ ...drsData, originalCollection });
                        
                        // 編集モード開始時に自動的に開く
                        if (!expandedItems.has(index)) {
                          const newExpandedItems = new Set(expandedItems);
                          newExpandedItems.add(index);
                          setExpandedItems(newExpandedItems);
                        }
                      }
                    }}
                  >
                    {editingIndex === index ? uiText("Cancel") : uiText("Edit")}
                  </button>
                  {editingIndex === index && (
                    <button
                      style={{
                        backgroundColor: 'var(--workspace-primary, #cfae91)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '6px 12px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--workspace-primary-dark, #b89a7a)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
                      }}
                      onClick={() => {
                        if (editingData) {
                          // バリデーション実行
                          const validation = validateDRSData(editingData);

                          if (!validation.isValid) {
                            // エラーメッセージを表示
                            const errorMessage = validation.errors.join('\n');
                            alert(uiText("Validation error:\n{{errorMessage}}", { errorMessage: errorMessage }));
                            return;
                          }

                          // DBに保存
                          const saveToDatabase = async () => {
                            try {
                              if (!editingData.pageId) {
                                alert(uiText("Page ID not found"));
                                return;
                              }

                              // 保存するデータを準備
                              const saveData = {
                                pageId: editingData.pageId,
                                defaultText: editingData.defaultText,
                                template: editingData.template,
                                slots: editingData.slots,
                                editable: editingData.editable,
                                updatedAt: serverTimestamp()
                              };

                              // 公開ページのステータスに応じてコレクションを特定（データ取得時と同じロジック）
                              const isPublic = publicPageData?.status === 'public' && publicPageData?.published === true;
                              const collectionName = isPublic ? 'publicDRS' : 'nonPublicDRS';

                              if (editingData.originalCollection === 'new') {
                                // 新規作成の場合：ドキュメントIDを生成してsetDocを使用
                                const documentId = generateDRSDocumentId(editingData.pageId, editingData);
                                const docRef = getDrsDocRef(collectionName, documentId);
                                await setDoc(docRef, saveData);
                              } else {
                                // 既存データの更新の場合：updateDocを使用（言語別パス対応）
                                const docRef = getDrsDocRef(collectionName, editingData.docId || editingData.pageId);
                                await updateDoc(docRef, saveData);
                              }

                              alert(uiText("Data saved successfully"));

                              // 編集モードを終了
                              setEditingIndex(null);
                              setEditingData(null);

                              // データを再読み込み
                              await fetchData();
                            } catch (error) {
                              console.error('DB保存エラー:', error);
                              alert(uiText("Failed to save data"));
                            }
                          };

                          saveToDatabase();
                        }
                      }}
                    >
                      {uiText("Register")}
                    </button>
                  )}
                  <button
                    style={{
                      backgroundColor: '#f8d7da',
                      color: '#721c24',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5c6cb';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8d7da';
                    }}
                    onClick={async () => {
                      if (window.confirm(uiText("Delete this data? This cannot be undone."))) {
                        try {
                          // コレクション名を決定
                          const isPublic = publicPageData?.status === 'public' && publicPageData?.published === true;
                          const collectionName = isPublic ? 'publicDRS' : 'nonPublicDRS';

                          // ドキュメントを削除（言語別パス対応）
                          const docRef = getDrsDocRef(collectionName, drsData.docId || drsData.pageId);
                          await deleteDoc(docRef);

                          alert(uiText("Data deleted successfully"));

                          // データを再読み込み
                          await fetchData();
                        } catch (error) {
                          console.error('データ削除エラー:', error);
                          alert(uiText("Failed to delete data"));
                        }
                      }
                    }}
                  >
                    {uiText("Delete")}
                  </button>
                </div>
              </div>
              
              {/* 詳細表示（開閉状態に応じて表示） */}
              {expandedItems.has(index) && (
                <>
                  {/* DefaultText表示 */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{
                  fontSize: '12px',
                  color: '#495057',
                  fontWeight: '600',
                  marginBottom: '4px'
                }}>
                  Default Text:
                </div>
                <input
                  type="text"
                  value={editingIndex === index ? (editingData?.defaultText || '') : (drsData.defaultText || '')}
                  onChange={(e) => {
                    if (editingIndex === index && editingData) {
                      setEditingData({ ...editingData, defaultText: e.target.value });
                    }
                  }}
                  disabled={editingIndex !== index}
                  style={{
                    width: '100%',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    backgroundColor: editingIndex === index ? '#ffffff' : '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    padding: '12px',
                    wordBreak: 'break-word',
                    opacity: editingIndex === index ? 1 : 0.8,
                    cursor: editingIndex === index ? 'text' : 'not-allowed',
                    boxSizing: 'border-box'
                  }}
                  placeholder={uiText("Default text is not set")}
                />
              </div>

              {/* Editable表示 */}
              {drsData.editable !== undefined && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{
                    fontSize: '12px',
                    color: '#495057',
                    fontWeight: '600',
                    marginBottom: '4px'
                  }}>
                    Editable:
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: editingIndex === index ? 1 : 0.8
                  }}>
                    <select
                      value={editingIndex === index ? (editingData?.editable ? 'true' : 'false') : String(drsData.editable)}
                      onChange={(e) => {
                        if (editingIndex === index && editingData) {
                          setEditingData({ ...editingData, editable: e.target.value === 'true' });
                        }
                      }}
                      disabled={editingIndex !== index}
                      style={{
                        cursor: editingIndex === index ? 'pointer' : 'not-allowed',
                        fontSize: '14px',
                        padding: '4px 8px',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        backgroundColor: editingIndex === index ? '#ffffff' : '#f8f9fa',
                        opacity: editingIndex === index ? 1 : 0.8
                      }}
                    >
                      <option value="true">true</option>
                      <option value="false">false</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Template表示 */}
              {drsData.template && (
                <div>
                  <div style={{
                    fontSize: '12px',
                    color: '#495057',
                    fontWeight: '600',
                    marginBottom: '4px'
                  }}>
                    Template:
                  </div>
                  <div style={{
                    width: '100%',
                    minHeight: '40px',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    backgroundColor: editingIndex === index ? '#ffffff' : '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    padding: '8px',
                    wordBreak: 'break-word',
                    fontFamily: 'var(--font-mono)',
                    opacity: editingIndex === index ? 1 : 0.8,
                    cursor: editingIndex === index ? 'text' : 'not-allowed',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '2px',
                    boxSizing: 'border-box'
                  }}>
                    {editingIndex === index ? (
                      // 編集モード：ドラッグ&ドロップ対応のトークンベース表示
                      ((() => {
                        const template = editingData?.template || '';
                        const tokens = parseTemplate(template);
                        
                        return (
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={(event) => handleDragEnd(event, index)}
                          >
                            <SortableContext
                              items={tokens.map((token, i) => `token-${token.value}-${i}`)}
                              strategy={horizontalListSortingStrategy}
                            >
                              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px' }}>
                                {/* 先頭にテキスト挿入ボタン */}
                                <TextInsertButton
                                  position={uiText("At the start")}
                                  onInsert={(text) => {
                                    if (editingData) {
                                      const newTemplate = text + (editingData.template || '');
                                      setEditingData({ ...editingData, template: newTemplate });
                                    }
                                  }}
                                />
                                
                                {tokens.map((token, tokenIndex) => (
                                  <React.Fragment key={`${token.type}-${tokenIndex}-${token.value}`}>
                                    {token.type === 'key' ? (
                                      <DraggableKey
                                        token={token}
                                        tokenIndex={tokenIndex}
                                        onDelete={() => {
                                          if (editingData) {
                                            const newTokens = tokens.filter((_, i) => i !== tokenIndex);
                                            const newTemplate = tokensToTemplate(newTokens);
                                            
                                            // 削除されたキーをSlotsからも削除
                                            const keyToDelete = token.key;
                                            const newSlots = { ...editingData.slots };
                                            if (keyToDelete && newSlots[keyToDelete]) {
                                              delete newSlots[keyToDelete];
                                            }
                                            
                                            setEditingData({ 
                                              ...editingData, 
                                              template: newTemplate,
                                              slots: newSlots
                                            });
                                          }
                                        }}
                                      />
                                    ) : (
                                      <DraggableText
                                        token={token}
                                        tokenIndex={tokenIndex}
                                        onUpdate={(newValue) => {
                                          if (editingData) {
                                            const newTokens = [...tokens];
                                            newTokens[tokenIndex] = { ...token, value: newValue };
                                            const newTemplate = tokensToTemplate(newTokens);
                                            setEditingData({ ...editingData, template: newTemplate });
                                          }
                                        }}
                                        onDelete={() => {
                                          if (editingData) {
                                            const newTokens = tokens.filter((_, i) => i !== tokenIndex);
                                            const newTemplate = tokensToTemplate(newTokens);
                                            setEditingData({ ...editingData, template: newTemplate });
                                          }
                                        }}
                                      />
                                    )}
                                    
                                    {/* 各トークンの後にテキスト挿入ボタン */}
                                    <TextInsertButton
                                      position={uiText("#{{v0}}", { v0: tokenIndex + 1 })}
                                      onInsert={(text) => {
                                        if (editingData) {
                                          const newTokens = [...tokens];
                                          newTokens.splice(tokenIndex + 1, 0, { type: 'text', value: text });
                                          const newTemplate = tokensToTemplate(newTokens);
                                          setEditingData({ ...editingData, template: newTemplate });
                                        }
                                      }}
                                    />
                                  </React.Fragment>
                                ))}
                                
                                {/* キー追加用の入力フィールド */}
                                <input
                                  type="text"
                                  placeholder={uiText("+ Enter a key name")}
                                  style={{
                                    fontSize: '12px',
                                    padding: '2px 6px',
                                    border: '1px dashed #ccc',
                                    borderRadius: '4px',
                                    backgroundColor: 'var(--workspace-border-soft, #f5f0eb)',
                                    cursor: 'text',
                                    minWidth: '100px'
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                                      if (editingData) {
                                        const keyName = e.currentTarget.value.trim();

                                        // Template内の既存キーをチェック
                                        const existingKeys = getKeyOrderFromTemplate(editingData.template || '');

                                        if (existingKeys.includes(keyName)) {
                                          alert(uiText("Key \"{{keyName}}\" already exists in Template.", { keyName: keyName }));
                                          e.currentTarget.value = ''; // リセット
                                          return;
                                        }

                                        const newTemplate = (editingData.template || '') + `{${keyName}}`;

                                        // 新しいキーをSlotsに追加
                                        const newSlots = { ...editingData.slots };

                                        if (!newSlots[keyName]) {
                                          // 既存のdefault値があるかチェック
                                          const hasDefault = Object.values(newSlots).some(slotArray => 
                                            Array.isArray(slotArray) && slotArray.some(item => item.default === true)
                                          );

                                          const newSlotItem = {
                                            default: hasDefault ? false : true,
                                            presence: '',
                                            equivalents: ['']
                                          };

                                          newSlots[keyName] = [newSlotItem];
                                        }

                                        const updatedEditingData = { 
                                          ...editingData, 
                                          template: newTemplate,
                                          slots: newSlots
                                        };

                                        setEditingData(updatedEditingData);
                                        e.currentTarget.value = ''; // リセット
                                      } else {}
                                    }
                                  }}
                                  onBlur={(e) => {
                                    if (e.currentTarget.value.trim()) {
                                      if (editingData) {
                                        const keyName = e.currentTarget.value.trim();

                                        // Template内の既存キーをチェック
                                        const existingKeys = getKeyOrderFromTemplate(editingData.template || '');

                                        if (existingKeys.includes(keyName)) {
                                          alert(uiText("Key \"{{keyName}}\" already exists in Template.", { keyName: keyName }));
                                          e.currentTarget.value = ''; // リセット
                                          return;
                                        }

                                        const newTemplate = (editingData.template || '') + `{${keyName}}`;

                                        // 新しいキーをSlotsに追加
                                        const newSlots = { ...editingData.slots };

                                        if (!newSlots[keyName]) {
                                          // 既存のdefault値があるかチェック
                                          const hasDefault = Object.values(newSlots).some(slotArray => 
                                            Array.isArray(slotArray) && slotArray.some(item => item.default === true)
                                          );

                                          const newSlotItem = {
                                            default: hasDefault ? false : true,
                                            presence: '',
                                            equivalents: ['']
                                          };

                                          newSlots[keyName] = [newSlotItem];
                                        }

                                        const updatedEditingData = { 
                                          ...editingData, 
                                          template: newTemplate,
                                          slots: newSlots
                                        };

                                        setEditingData(updatedEditingData);
                                        e.currentTarget.value = ''; // リセット
                                      } else {}
                                    }
                                  }}
                                />
                              </div>
                            </SortableContext>
                          </DndContext>
                        );
                      })())
                    ) : (
                      // 表示モード：通常のテキスト表示
                      (drsData.template)
                    )}
                  </div>
                </div>
              )}
              
              {/* Slots表示 */}
              {drsData.slots && (
                <div style={{
                  marginTop: '16px',
                  backgroundColor: '#ffffff', // White background for slots section
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  padding: '12px',
                  width: '100%',
                  boxSizing: 'border-box'
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '12px',
                    color: '#212529'
                  }}>
                    Slots:
                  </div>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px'
                  }}>
                    {(() => {
                      // Templateのキー順に基づいてSlotsを並び替え
                      const template = editingIndex === index ? (editingData?.template || '') : (drsData.template || '');
                      const slots = editingIndex === index ? (editingData?.slots || {}) : (drsData.slots || {});
                      const orderedSlots = sortSlotsByTemplateOrder(slots, template);
                      
                      return orderedSlots.map(([slotKey, slotValue]) => (
                        // Container for each slot type (e.g., "target", "action")
                        (<div key={slotKey} style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #e9ecef', // Border for each slot type
                          borderRadius: '8px',
                          backgroundColor: '#ffffff', // White background for slot type
                          boxSizing: 'border-box'
                        }}>
                          {/* Slot type label area */}
                          <div style={{
                            fontSize: '15px',
                            fontWeight: '700',
                            color: '#495057', // Dark gray for slot type label
                            marginBottom: '12px',
                            borderBottom: '1px solid #e9ecef', // Separator for slot type label
                            paddingBottom: '8px'
                          }}>
                            {slotKey}:
                          </div>
                          {/* Data area for the current slot type */}
                          <div style={{ paddingLeft: '8px' }}> {/* Indent content */}
                          {Array.isArray(slotValue) ? (
                            <>
                              {slotValue.map((item, itemIndex) => (
                                // Container for each item within the slot type array (e.g., "項目 1")
                                (<div key={itemIndex} style={{
                                  backgroundColor: '#ffffff',
                                  border: '1px solid #e9ecef',
                                  borderRadius: '4px',
                                  padding: '10px',
                                  marginBottom: '12px', // Space between items
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)', // Subtle shadow for item box
                                  boxSizing: 'border-box'
                                }}>
                                  <div style={{
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    color: '#495057',
                                    marginBottom: '10px',
                                    borderBottom: '1px dashed #e9ecef', // Dashed separator for item label
                                    paddingBottom: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                  }}>
                                    <span>{uiText("Item")} {itemIndex + 1}:</span>
                                    
                                    {/* 項目削除ボタン */}
                                    {editingIndex === index && (
                                      <button
                                        onClick={() => {
                                          if (editingData) {
                                            const newEditingData = { ...editingData };
                                            if (!newEditingData.slots) newEditingData.slots = {};
                                            if (!newEditingData.slots[slotKey]) newEditingData.slots[slotKey] = [...slotValue];
                                            
                                            // 指定されたインデックスの項目を削除
                                            newEditingData.slots[slotKey].splice(itemIndex, 1);
                                            setEditingData(newEditingData);
                                          }
                                        }}
                                        style={{
                                          backgroundColor: '#f8d7da',
                                          color: '#721c24',
                                          border: 'none',
                                          borderRadius: '4px',
                                          padding: '2px 6px',
                                          fontSize: '12px',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          minWidth: '20px',
                                          height: '20px'
                                        }}
                                        onMouseOver={(e) => {
                                          e.currentTarget.style.backgroundColor = '#f5c6cb';
                                        }}
                                        onMouseOut={(e) => {
                                          e.currentTarget.style.backgroundColor = '#f8d7da';
                                        }}
                                        title={uiText("Delete item {{v0}}", { v0: itemIndex + 1 })}
                                      >
                                        ×
                                      </button>
                                    )}
                                  </div>
                                  {/* Individual fields within the item */}
                                  <div style={{ paddingLeft: '8px' }}> {/* Indent fields */}
                                  {item.default !== undefined && (
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', opacity: editingIndex === index ? 1 : 0.8 }}>
                                      <div style={{ minWidth: '100px', fontWeight: '600', color: '#495057', fontSize: '13px' }}>
                                        default:
                                      </div>
                                      <div style={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                      }}>
                                        <select
                                          value={editingIndex === index ? 
                                            (editingData?.slots?.[slotKey]?.[itemIndex]?.default ? 'true' : 'false') : 
                                            (item.default ? 'true' : 'false')
                                          }
                                          onChange={(e) => {
                                            if (editingIndex === index && editingData) {
                                              const newEditingData = { ...editingData };
                                              if (!newEditingData.slots) newEditingData.slots = {};
                                              if (!newEditingData.slots[slotKey]) newEditingData.slots[slotKey] = [...slotValue];
                                              newEditingData.slots[slotKey][itemIndex] = {
                                                ...newEditingData.slots[slotKey][itemIndex],
                                                default: e.target.value === 'true'
                                              };
                                              setEditingData(newEditingData);
                                            }
                                          }}
                                          disabled={editingIndex !== index}
                                          style={{
                                            cursor: editingIndex === index ? 'pointer' : 'not-allowed',
                                            fontSize: '13px',
                                            padding: '4px 8px',
                                            border: '1px solid #ced4da',
                                            borderRadius: '4px',
                                            backgroundColor: editingIndex === index ? '#ffffff' : '#f8f9fa',
                                            opacity: editingIndex === index ? 1 : 0.8,
                                            fontFamily: 'var(--font-mono)'
                                          }}
                                        >
                                          <option value="true">true</option>
                                          <option value="false">false</option>
                                        </select>
                                      </div>
                                    </div>
                                  )}
                                  {item.presence !== undefined && (
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', opacity: editingIndex === index ? 1 : 0.8 }}>
                                      <div style={{ minWidth: '100px', fontWeight: '600', color: '#495057', fontSize: '13px' }}>
                                        presence:
                                      </div>
                                      <input
                                        type="text"
                                        value={editingIndex === index ? 
                                          (editingData?.slots?.[slotKey]?.[itemIndex]?.presence || '') : 
                                          (item.presence || '')
                                        }
                                        onChange={(e) => {
                                          if (editingIndex === index && editingData) {
                                            const newEditingData = { ...editingData };
                                            if (!newEditingData.slots) newEditingData.slots = {};
                                            if (!newEditingData.slots[slotKey]) newEditingData.slots[slotKey] = [...slotValue];
                                            newEditingData.slots[slotKey][itemIndex] = {
                                              ...newEditingData.slots[slotKey][itemIndex],
                                              presence: e.target.value
                                            };
                                            setEditingData(newEditingData);
                                          }
                                        }}
                                        disabled={editingIndex !== index}
                                        style={{
                                          flex: 1,
                                          backgroundColor: editingIndex === index ? '#ffffff' : '#f8f9fa',
                                          border: '1px solid #ced4da',
                                          borderRadius: '4px',
                                          padding: '6px 8px',
                                          fontSize: '13px',
                                          wordBreak: 'break-word',
                                          fontFamily: 'var(--font-mono)',
                                          opacity: editingIndex === index ? 1 : 0.8,
                                          cursor: editingIndex === index ? 'text' : 'not-allowed',
                                          boxSizing: 'border-box'
                                        }}
                                        placeholder={uiText("Enter a presence value")}
                                      />
                                    </div>
                                  )}
                                  {item.equivalents !== undefined && (
                                    <div style={{ marginBottom: '8px', opacity: editingIndex === index ? 1 : 0.8 }}>
                                      <div style={{ fontWeight: '600', color: '#495057', fontSize: '13px', marginBottom: '4px' }}>
                                        equivalents:
                                      </div>
                                      <div style={{ paddingLeft: '16px' }}> {/* Indent equivalents list */}
                                        {item.equivalents.map((eq: string, eqIndex: number) => (
                                          <div key={eqIndex} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px', gap: '4px' }}>
                                            <div style={{ minWidth: '30px', fontWeight: '500', color: '#6c757d', fontSize: '13px' }}>
                                              {eqIndex}:
                                            </div>
                                            <input
                                              type="text"
                                              value={editingIndex === index ? 
                                                (editingData?.slots?.[slotKey]?.[itemIndex]?.equivalents?.[eqIndex] || '') : 
                                                (eq || '')
                                              }
                                              onChange={(e) => {
                                                if (editingIndex === index && editingData) {
                                                  const newEditingData = { ...editingData };
                                                  if (!newEditingData.slots) newEditingData.slots = {};
                                                  if (!newEditingData.slots[slotKey]) newEditingData.slots[slotKey] = [...slotValue];
                                                  if (!newEditingData.slots[slotKey][itemIndex].equivalents) {
                                                    newEditingData.slots[slotKey][itemIndex].equivalents = [...item.equivalents];
                                                  }
                                                  newEditingData.slots[slotKey][itemIndex].equivalents[eqIndex] = e.target.value;
                                                  setEditingData(newEditingData);
                                                }
                                              }}
                                              disabled={editingIndex !== index}
                                              style={{
                                                flex: 1,
                                                backgroundColor: editingIndex === index ? '#ffffff' : '#f8f9fa',
                                                border: '1px solid #ced4da',
                                                borderRadius: '4px',
                                                padding: '6px 8px',
                                                fontSize: '13px',
                                                wordBreak: 'break-word',
                                                fontFamily: 'var(--font-mono)',
                                                opacity: editingIndex === index ? 1 : 0.8,
                                                cursor: editingIndex === index ? 'text' : 'not-allowed',
                                                boxSizing: 'border-box'
                                              }}
                                              placeholder={uiText("Enter equivalents[{{eqIndex}}]", { eqIndex: eqIndex })}
                                            />
                                            
                                            {/* equivalents削除ボタン */}
                                            {editingIndex === index && (
                                              <button
                                                onClick={() => {
                                                  if (editingData) {
                                                    const newEditingData = { ...editingData };
                                                    if (!newEditingData.slots) newEditingData.slots = {};
                                                    if (!newEditingData.slots[slotKey]) newEditingData.slots[slotKey] = [...slotValue];
                                                    if (!newEditingData.slots[slotKey][itemIndex].equivalents) {
                                                      newEditingData.slots[slotKey][itemIndex].equivalents = [...item.equivalents];
                                                    }
                                                    // 指定されたインデックスの要素を削除
                                                    newEditingData.slots[slotKey][itemIndex].equivalents.splice(eqIndex, 1);
                                                    setEditingData(newEditingData);
                                                  }
                                                }}
                                                style={{
                                                  backgroundColor: '#f8d7da',
                                                  color: '#721c24',
                                                  border: 'none',
                                                  borderRadius: '4px',
                                                  padding: '4px 6px',
                                                  fontSize: '12px',
                                                  cursor: 'pointer',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  minWidth: '24px',
                                                  height: '24px'
                                                }}
                                                onMouseOver={(e) => {
                                                  e.currentTarget.style.backgroundColor = '#f5c6cb';
                                                }}
                                                onMouseOut={(e) => {
                                                  e.currentTarget.style.backgroundColor = '#f8d7da';
                                                }}
                                                title={uiText("Delete equivalents[{{eqIndex}}]", { eqIndex: eqIndex })}
                                              >
                                                ×
                                              </button>
                                            )}
                                          </div>
                                        ))}
                                        
                                        {/* equivalents追加ボタン */}
                                        {editingIndex === index && (
                                          <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                                            <button
                                              onClick={() => {
                                                if (editingData) {
                                                  const newEditingData = { ...editingData };
                                                  if (!newEditingData.slots) newEditingData.slots = {};
                                                  if (!newEditingData.slots[slotKey]) newEditingData.slots[slotKey] = [...slotValue];
                                                  if (!newEditingData.slots[slotKey][itemIndex].equivalents) {
                                                    newEditingData.slots[slotKey][itemIndex].equivalents = [...item.equivalents];
                                                  }
                                                  newEditingData.slots[slotKey][itemIndex].equivalents.push('');
                                                  setEditingData(newEditingData);
                                                }
                                              }}
                                              style={{
                                                backgroundColor: 'var(--workspace-primary, #cfae91)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                padding: '4px 8px',
                                                fontSize: '12px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                              }}
                                              onMouseOver={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--workspace-primary-dark, #b89a7a)';
                                              }}
                                              onMouseOut={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
                                              }}
                                            >
                                              <span style={{ fontSize: '14px', fontWeight: 'bold' }}>+</span>
                                              <span>{uiText("Add equivalents")}</span>
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                </div>)
                              ))}
                              
                              {/* presence追加ボタン */}
                              {editingIndex === index && (
                                <div style={{ marginTop: '8px' }}>
                                  <button
                                    onClick={() => {
                                      if (editingData) {
                                        const newEditingData = { ...editingData };
                                        if (!newEditingData.slots) newEditingData.slots = {};
                                        if (!newEditingData.slots[slotKey]) newEditingData.slots[slotKey] = [...slotValue];
                                        
                                        // 新しい項目を追加
                                        const newItem = {
                                          default: false,
                                          presence: '',
                                          equivalents: ['']
                                        };
                                        newEditingData.slots[slotKey].push(newItem);
                                        setEditingData(newEditingData);
                                      }
                                    }}
                                    style={{
                                      backgroundColor: 'var(--workspace-primary, #cfae91)',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      padding: '6px 12px',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px'
                                    }}
                                    onMouseOver={(e) => {
                                      e.currentTarget.style.backgroundColor = 'var(--workspace-primary-dark, #b89a7a)';
                                    }}
                                    onMouseOut={(e) => {
                                      e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
                                    }}
                                  >
                                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>+</span>
                                    <span>{uiText("Add presence")}</span>
                                  </button>
                                </div>
                              )}
                            </>
                          ) : (
                            <pre style={{
                              backgroundColor: '#ffffff',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              padding: '10px',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              fontSize: '13px',
                              fontFamily: 'var(--font-mono)'
                            }}>
                              {JSON.stringify(slotValue, null, 2)}
                            </pre>
                          )}
                        </div>
                        </div>)
                    ));
                    })()}
                  </div>
                </div>
              )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {/* DRSデータ追加ボタン */}
      {!loading && !error && (
        <div style={{
          marginTop: '16px',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {can('drs.manualEdit') && (
          <button
            onClick={() => {
              if (!workspaceId || !boardId) {
                alert(uiText("Workspace ID or board ID not found"));
                return;
              }
              
              // 新しいDRSデータを作成
              const newDrsData: DRSData = {
                pageId: boardId,
                defaultText: '',
                template: '{action}{target}', // デフォルトのテンプレートを設定
                editable: true,
                slots: {
                  action: [{
                    default: true,
                    presence: '',
                    equivalents: ['']
                  }, {
                    default: false,
                    presence: '',
                    equivalents: ['']
                  }],
                  target: [{
                    default: true,
                    presence: '',
                    equivalents: ['']
                  }, {
                    default: false,
                    presence: '',
                    equivalents: ['']
                  }]
                }
              };
              
              // 編集モードで新しいデータを追加
              setDrsDataList([...drsDataList, newDrsData]);
              setEditingIndex(drsDataList.length);
              setEditingData({ ...newDrsData, originalCollection: 'new' });
            }}
            style={{
              backgroundColor: '#ffffff',
              color: 'var(--workspace-primary, #cfae91)',
              border: '1px solid var(--workspace-primary, #cfae91)',
              borderRadius: '4px',
              padding: '6px 12px',
              fontSize: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--workspace-border-soft, #f5f0eb)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
            }}
          >
            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>+</span>
            <span>{uiText("Add DRS manually")}</span>
          </button>
          )}

          {can('drs.aiGenerate') && (
          <button
            onClick={startAIGenerationForThisPage}
            disabled={aiGenerating || !workspaceId || !boardId}
            style={{
              backgroundColor: aiGenerating ? '#ccc' : 'var(--workspace-primary, #cfae91)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '6px 12px',
              fontSize: '12px',
              cursor: (aiGenerating || !workspaceId || !boardId) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseOver={(e) => {
              if (aiGenerating || !workspaceId || !boardId) return;
              e.currentTarget.style.backgroundColor = 'var(--workspace-primary-dark, #b89a7a)';
            }}
            onMouseOut={(e) => {
              if (aiGenerating || !workspaceId || !boardId) return;
              e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
            }}
            title={uiText("Generate DRS with AI for this page only")}
          >
            {aiGenerating && (
              <span style={{
                display: 'inline-block',
                width: '14px',
                height: '14px',
                border: '2px solid #fff',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }} />
            )}
            <span>{aiGenerating ? uiText("Generating...") : uiText("Generate with DRS AI")}</span>
          </button>
          )}
          </div>
        </div>
      )}

      {/* AI生成の結果メッセージ */}
      {(aiGenError || aiGenSuccess) && (
        <div style={{
          marginTop: '10px',
          width: '100%',
          maxWidth: '900px',
          padding: '10px 12px',
          borderRadius: '6px',
          border: `1px solid ${aiGenError ? '#f44336' : 'var(--workspace-primary, #cfae91)'}`,
          backgroundColor: aiGenError ? '#ffebee' : 'var(--workspace-border-soft, #f5f0eb)',
          color: aiGenError ? '#b71c1c' : 'var(--workspace-primary-dark, #8b6f47)',
          fontSize: '13px',
          boxSizing: 'border-box'
        }}>
          {aiGenError ? `✗ ${aiGenError}` : `✓ ${aiGenSuccess}`}
        </div>
      )}
      </div>
    </>
  );
};

export default DRSView;
